import { useCallback, useEffect, useRef } from 'react'
import { View } from 'react-native'
import { Stack, useRouter } from 'expo-router'
import Head from 'expo-router/head'
import { StatusBar } from 'expo-status-bar'
import { ScreenLoadingFallback } from '@/src/components/ui/ScreenLoadingFallback'
import { useAppSession } from '@/src/contexts/AppSessionContext'

import { PurchaseContent } from '../components/PurchaseContent'
import { PurchaseErrorNotice } from '../components/PurchaseErrorNotice'
import { PurchaseHero } from '../components/PurchaseHero'
import { PurchaseOptionsSection } from '../components/PurchaseOptionsSection'
import { PurchaseScreenCard } from '../components/PurchaseScreenCard'
import { usePurchaseScreenController } from '../hooks/usePurchaseScreenController'
import { openPurchaseUrl } from '../utils/openPurchaseUrl'
import { resolvePurchaseRevenueCatUserId } from '../utils/purchaseScreenIdentity'

export default function Purchase() {
  const { push, replace } = useRouter()
  const { skipPaywall, accountRoute } = useAppSession()
  const skipNavigateAfterMainRef = useRef(false)

  const {
    backendUser,
    cardAnimatedStyle,
    copy,
    dispatch,
    formatCurrency,
    handleOptionsChange,
    handlePurchase,
    heroHeight,
    isCompactLayout,
    isRevenueCatTestStore,
    isSessionLoading,
    labels,
    language,
    purchaseMutation,
    retryRevenueCatInitialization,
    revenueCatInitStatus,
    state,
    isProcessing,
  } = usePurchaseScreenController()

  useEffect(() => {
    if (!skipNavigateAfterMainRef.current) {
      return
    }
    if (accountRoute !== 'main') {
      return
    }
    skipNavigateAfterMainRef.current = false
    replace('/(protected)/(account)/(tabs)')
  }, [accountRoute, replace])

  const handleSkip = useCallback(() => {
    skipNavigateAfterMainRef.current = true
    skipPaywall()
  }, [skipPaywall])

  const cardBottomPadding = isCompactLayout ? 0 : 32

  if (isSessionLoading || revenueCatInitStatus === 'idle') {
    return <ScreenLoadingFallback />
  }

  return (
    <View className="flex-1 bg-background-primary">
      <Head>
        <title>{labels.pageTitle}</title>
      </Head>
      <StatusBar style="light" />
      <Stack.Screen options={{ headerShown: false }} />

      <PurchaseHero
        heroHeight={heroHeight}
        isRevenueCatTestStore={isRevenueCatTestStore}
        testStoreLabel={labels.testStoreBadge}
      />

      <PurchaseScreenCard
        bottomPadding={cardBottomPadding}
        cardAnimatedStyle={cardAnimatedStyle}
        headline={copy.paymentHeadline}
        description={copy.paymentDescription}
        isCompactLayout={isCompactLayout}
        onLayout={(height) =>
          dispatch({ type: 'setCardHeight', value: height })
        }
      >
        <PurchaseContent
          isCompactLayout={isCompactLayout}
          isProcessing={isProcessing}
          isPurchasePending={purchaseMutation.isPending}
          onSkip={handleSkip}
          skipLabel={labels.skip}
          onOpenFirstLegalUrl={() => openPurchaseUrl(copy.paymentFirstLegalUrl)}
          onOpenSecondLegalUrl={() =>
            openPurchaseUrl(copy.paymentSecondLegalUrl)
          }
          onPurchase={handlePurchase}
          onVoucherPress={() => push('/purchase/voucher/redeem')}
          optionsContent={
            <PurchaseOptionsSection
              isCompactLayout={isCompactLayout}
              isSessionLoading={isSessionLoading}
              revenueCatInitStatus={revenueCatInitStatus}
              revenueCatUserId={resolvePurchaseRevenueCatUserId(
                backendUser?.revenueCatId,
              )}
              language={language}
              paymentChoosePlanText={copy.paymentChoosePlanText}
              formatCurrency={formatCurrency}
              yearlySavingsText={copy.yearlySavingsText}
              onRetryRevenueCatInitialization={retryRevenueCatInitialization}
              onOptionsChange={handleOptionsChange}
            />
          }
          paymentFirstLegalText={copy.paymentFirstLegalText}
          paymentLegalAndText={copy.paymentLegalAndText}
          paymentLegalPrefixText={copy.paymentLegalPrefixText}
          paymentPrimaryButtonText={copy.paymentPrimaryButtonText}
          paymentSecondLegalText={copy.paymentSecondLegalText}
          purchaseErrorNotice={
            <PurchaseErrorNotice
              title={state.purchaseFeedback?.title ?? ''}
              message={state.purchaseFeedback?.message ?? ''}
            />
          }
          purchaseFeedback={state.purchaseFeedback}
          selectedPackage={state.selectedPackage}
          voucherLabel={labels.voucher}
        />
      </PurchaseScreenCard>
    </View>
  )
}
